import { CLOCK_TEXT } from '../../lib/clockTheme'
import type { DraftState } from '../../types/protocol'

// The board's clock. Same states as the DraftRoom clock (running / paused /
// wait-policy expiry), but every dimension here is viewport-relative: this is
// the single biggest thing on the screen and a fixed rem would shrink it to
// half its intended physical size on a 4K panel.
//
// Tabular figures matter more than they look — without them the digits jitter
// horizontally every tick, which is very visible at this size.
//
// Colours come from lib/clockTheme so the board and the draft room agree. They
// used to disagree: this file reused *position* colours (red = QB fill, amber =
// TE fill) for crit/warn while the room used its own amber/rose, so the same
// eight seconds looked like two different warnings on two screens in the same
// room.

export interface ClockProps {
  status: DraftState['status']
  remainingMs: number | null
  expired: boolean
  pausedRemainingMs: number | null
  deadlineAt: string | null
  pickSeconds: number
}

export default function ClockDisplay(props: ClockProps) {
  const { text, tone, label } = clockFace(props)

  return (
    <div className="flex flex-col items-center leading-none">
      <p
        className={`text-board-clock font-display leading-none font-bold tabular-nums ${tone}`}
      >
        {text}
      </p>
      {label && (
        <p className="text-board-label mt-[0.4vh] font-mono font-semibold tracking-[0.3em] text-ink-lo uppercase">
          {label}
        </p>
      )}
    </div>
  )
}

export interface ClockFace {
  text: string
  /** Tailwind text colour class. */
  tone: string
  /** Small caption under the digits; null when the digits speak for themselves. */
  label: string | null
}

export function clockFace({
  status,
  remainingMs,
  expired,
  pausedRemainingMs,
  deadlineAt,
  pickSeconds,
}: ClockProps): ClockFace {
  if (status === 'paused') {
    // Frozen at whatever was left, so the number alone would look like a
    // stopped clock. The caption is what tells the room it is deliberate.
    const secs = pausedRemainingMs != null ? Math.ceil(pausedRemainingMs / 1000) : pickSeconds
    return { text: formatMmSs(secs), tone: CLOCK_TEXT.warn, label: 'Paused' }
  }

  // Wait policy: the timer is gone but the team can still pick, so there is no
  // countdown left to render.
  if (status === 'live' && deadlineAt == null) {
    return { text: '0:00', tone: CLOCK_TEXT.crit, label: 'Time expired' }
  }

  if (remainingMs == null) return { text: '--:--', tone: 'text-ink-lo', label: null }
  if (expired) return { text: '0:00', tone: CLOCK_TEXT.crit, label: 'Time expired' }

  // Same thresholds as lib/draftView's clockTone — crit at 10s, warn at 30s.
  const totalSecs = Math.ceil(remainingMs / 1000)
  const tone =
    totalSecs <= 10
      ? CLOCK_TEXT.crit
      : totalSecs <= 30
        ? CLOCK_TEXT.warn
        : CLOCK_TEXT.normal

  return { text: formatMmSs(totalSecs), tone, label: null }
}

export function formatMmSs(totalSecs: number): string {
  const safe = Math.max(0, totalSecs)
  const mins = Math.floor(safe / 60)
  const secs = safe % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
